import { ArrowRight01Icon, RankingIcon } from '@hugeicons/react'

import { RankingPodium } from '@/features/ranking/components/RankingPodium'
import { useRankingData } from '@/features/ranking/hooks/useRankingData'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Card } from '@/shared/ui/Card'
import { GameIcon } from '@/shared/ui/GameIcon'
import { SectionHeader } from '@/shared/ui/Layout'
import { ProgressBar } from '@/shared/ui/ProgressBar'

import { BlockEmptyState } from '../blocks/BlockEmptyState'
import { BlockSkeleton } from '../blocks/BlockSkeleton'
import { BlockGuidanceSheet } from '../blocks/guidance/ui/BlockGuidanceSheet'
import { GuidanceDoor } from '../blocks/guidance/ui/GuidanceDoor'
import { useBlockGuidance } from '../blocks/guidance/useBlockGuidance'

export const RankingPreview = () => {
  const { navigateTo } = useNavigation()
  const {
    top3,
    myRanking,
    isOnTopTier,
    topTierGap,
    topTierPercent,
    isEmpty,
    isLoading
  } = useRankingData()
  const guidance = useBlockGuidance('ranking')

  if (isLoading) return <BlockSkeleton rows={3} />

  /*
    Early return, e não `BlockEmptyState` dentro do `Card.Body`:
    `BlockEmptyState` traz o próprio `<Card className="card-gamification">`, e
    aninhá-lo daria cartão dentro de cartão — quebrando justamente o requisito
    de aparência única entre os estados vazios da home.
  */
  if (isEmpty)
    return (
      <>
        <BlockEmptyState
          icon={RankingIcon}
          title="Ranking ainda sem pontuação"
          hint="Assim que alguém pontuar, a classificação aparece aqui."
          action={
            guidance.hasGuidance ? (
              <GuidanceDoor
                label={guidance.doorLabel}
                onClick={guidance.onOpen}
              />
            ) : undefined
          }
        />

        <BlockGuidanceSheet
          open={guidance.open}
          onClose={guidance.onClose}
          title={guidance.sheetTitle}
          actions={guidance.actions}
        />
      </>
    )

  return (
    <Card className="card-gamification">
      <div className="px-4 pb-0.5 pt-3">
        {/* Ícone `stroke`, não `duotone`: era o único widget com ícone colorido.
            O link vem do `SectionHeader.Action`, que tem a métrica do CTA de
            rodapé do ObjectivesCard — antes era um `Button variant="link"`, com
            altura de CTA de página. */}
        <SectionHeader
          icon={<RankingIcon size={16} variant="stroke" aria-hidden />}
          title="Ranking geral"
        >
          <SectionHeader.Action onClick={() => navigateTo('/ranking')}>
            Ver ranking completo
          </SectionHeader.Action>
        </SectionHeader>
      </div>

      <Card.Body className="flex flex-col gap-2.5 px-4 pb-3 pt-1.5">
        <RankingPodium top3={top3} />

        {myRanking && (
          /* Coluna única, não fileira.
             A versão anterior punha quatro elementos ao lado de texto na
             mesma linha — coluna com rótulo+rank+experiência, texto de gap,
             progress bar e seta — contra o limite de UM de MASTER §7 L2. A
             375px isso comprimia "Faltam N pts para o Top 10" abaixo do piso
             de 32 caracteres do ACCESSIBILITY §2.4. A forma curta encurta o
             texto em um caractere — "Faltam N XP para o Top 10" —, então a
             folga só aumentou; "experiência" por extenso aqui a consumiria.

             Fundo cinza, não roxo: roxo passou a ser exclusivo da
             experiência. */
          <button
            type="button"
            onClick={() => navigateTo('/ranking')}
            className="flex w-full cursor-pointer flex-col gap-1.5 rounded-[0.625rem] border-0 bg-gray-100 px-3 py-2.5 text-left transition-colors hover:bg-gray-200"
          >
            <span className="flex items-center justify-between gap-2">
              <span className="text-caps font-bold uppercase tracking-wide text-gray-700">
                Sua posição
              </span>
              <ArrowRight01Icon
                size={16}
                className="shrink-0 text-gray-700"
                aria-hidden
              />
            </span>

            <span className="flex items-baseline gap-2">
              <span className="text-xl font-bold leading-none text-[var(--color-metric-points)]">
                #{myRanking.rank}
              </span>
              <span className="flex items-center gap-1 text-2xs font-semibold text-gray-700">
                <GameIcon name="star" size={10} />
                {myRanking.points} XP
              </span>
            </span>

            {topTierGap != null && topTierPercent != null && (
              <>
                <span className="text-xs font-semibold text-gray-700">
                  Faltam {topTierGap} XP para o Top 10
                </span>
                <ProgressBar
                  value={topTierPercent}
                  variant="primary"
                  size="sm"
                />
              </>
            )}

            {isOnTopTier && (
              <span className="text-xs font-semibold text-gray-700">
                Você está no Top 10! Continue pontuando.
              </span>
            )}
          </button>
        )}
      </Card.Body>
    </Card>
  )
}
